import type { TableCell, TableRow } from '../schemas'
import { copySourceLabel, numberedCopyText } from './copy-content'

export function cellCopyText(cell: TableCell | undefined, matterId: string, tableId: string, footnotes: boolean): string {
  if (!cell) return ''
  if (!footnotes) return cell.value
  return numberedCopyText(
    cell.segments.length ? cell.segments : [{ text: cell.value, reference_ids: [] }],
    new Map((cell.references ?? []).map((reference) => [reference.id, copySourceLabel(reference, matterId, tableId)]))
  )
}

export function copyCellMatrix(
  rows: TableRow[],
  columnIds: string[],
  matterId: string,
  tableId: string,
  footnotes: boolean,
  filenames: Map<string, string>
): string[][] {
  return rows.map((row) => {
    const cells = new Map(row.cells.map((cell) => [cell.column_id, cell]))
    return columnIds.map((id) =>
      id === 'row' ? row.source_filename ?? row.search_filename ?? filenames.get(row.id) ?? '' : cellCopyText(cells.get(id), matterId, tableId, footnotes)
    )
  })
}

/** Quoted TSV preserves tabs/newlines inside cells; HTML keeps spreadsheet cell boundaries explicit. */
export function cellClipboardContent(values: string[][]): { text: string; html: string } {
  const singleCell = values.length === 1 && values[0].length === 1
  const quote = (value: string) => (/[\t\n\r"]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value)
  const text = singleCell ? values[0][0] : values.map((row) => row.map(quote).join('\t')).join('\n')
  const table = document.createElement('table')
  for (const row of values) {
    const tr = table.insertRow()
    for (const value of row) {
      const td = tr.insertCell()
      td.style.whiteSpace = 'pre-wrap'
      // Prefix formula-like HTML values for spreadsheet paste; keep real numbers usable in calculations.
      // Plain-text paste remains exact and does not receive this spreadsheet protection.
      const numeric = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(value.trim())
      const protectedText = /^\s*[=+@-]/u.test(value) && !numeric
      td.textContent = protectedText ? "'" + value : value
      // Preserve the Office-specific declaration so Excel hides the protective apostrophe.
      if (protectedText) td.setAttribute('style', 'white-space: pre-wrap; mso-number-format: "\\@";')
    }
  }
  return { text, html: table.outerHTML }
}

export async function writeCellClipboard(values: string[][]): Promise<void> {
  const content = Promise.resolve(values).then(cellClipboardContent)
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
    // Start the write during the click gesture; Safari permits asynchronously prepared blobs.
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': content.then(({ text }) => new Blob([text], { type: 'text/plain' })),
        'text/html': content.then(({ html }) => new Blob([html], { type: 'text/html' })),
      }),
    ])
  } else {
    await navigator.clipboard.writeText((await content).text)
  }
}
